
import React, { useEffect, useRef } from 'react';
import { useClientLogoStore } from '@/lib/stores/clientLogoStore';
import { cn } from '@/lib/utils';
import { Spinner } from './ui/spinner';

const LogoCarousel = () => {
  const { logos, loading, error, fetchLogos } = useClientLogoStore();
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fetch logos from Supabase on component mount
  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);
  
  // Clone the logos array to use in the animation if we have logos
  const displayLogos = logos.length > 0 ? [...logos, ...logos] : [];
  
  // Create animation for the continuous scrolling effect
  useEffect(() => {
    if (!containerRef.current || logos.length === 0) return;
    
    const scrollSpeed = 30; // Pixels per second, adjust as needed
    let animationId: number;
    let startTime: number;
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      if (containerRef.current) {
        // Calculate the new scroll position
        const position = (elapsed * scrollSpeed) / 1000;
        
        // Reset when we've scrolled through half the items (the first set)
        if (position >= containerRef.current.scrollWidth / 2) {
          startTime = timestamp;
        } else {
          containerRef.current.scrollLeft = position;
        }
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [logos.length]);
  
  // If there's an error, show error message
  if (error) {
    console.error('Error loading logos:', error);
    return null;
  }
  
  // If there are no logos and we're not loading, don't render anything
  if (logos.length === 0 && !loading) return null;
  
  return (
    <section className="py-10 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-xl font-medium text-redflags-dark text-center mb-8">
          Trusted by companies worldwide
        </h2>
        
        {loading && logos.length === 0 ? (
          <div className="flex justify-center items-center py-10">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <div className="relative">
            <div 
              className="flex gap-8 items-center overflow-x-hidden whitespace-nowrap py-4"
              ref={containerRef}
            >
              {displayLogos.map((logo, index) => (
                <div 
                  key={`${logo.id}-${index}`} 
                  className={cn(
                    "shrink-0 h-16 flex items-center justify-center",
                    logo.url ? "cursor-pointer" : ""
                  )}
                  onClick={() => logo.url ? window.open(logo.url, "_blank") : null}
                >
                  <img 
                    src={logo.imageUrl} 
                    alt={logo.altText || logo.name} 
                    className="max-h-full max-w-[120px] object-contain"
                  />
                </div>
              ))}
            </div>
            
            {/* Gradient overlays for fade effect */}
            <div className="absolute top-0 left-0 h-full w-16 bg-gradient-to-r from-white to-transparent"></div>
            <div className="absolute top-0 right-0 h-full w-16 bg-gradient-to-l from-white to-transparent"></div>
          </div>
        )}
      </div>
    </section>
  );
};

export default LogoCarousel;
